import type { Component } from 'vue';
import { $t, type Translation } from '@teachino/shared-i18n';
import {
  type ModalV2Props,
  type ModalResult,
  useModalStore,
} from '../ModalV2/modal-store';
import FormulaDialog from './FormulaDialog.vue';

export type FormulaDialogProps = {
  initialLatex?: string;
} & ModalV2Props<string>;

export const formulaCheatSheet: {
  stringIdentifier: Translation;
  value: string;
}[] = [
  { stringIdentifier: 'katex.fractions', value: '\\frac{a}{b}' },
  { stringIdentifier: 'katex.exponents', value: 'x^2' },
  { stringIdentifier: 'katex.subscripts', value: 'x_1' },
  { stringIdentifier: 'katex.roots', value: '\\sqrt{x} / \\sqrt[n]{x}' },
  { stringIdentifier: 'katex.summation', value: '\\sum_{i=1}^{n} i^2' },
  { stringIdentifier: 'katex.product', value: '\\prod_{i=1}^{n} i' },
  { stringIdentifier: 'katex.limit', value: '\\lim_{x \\to 0} f(x)' },
  { stringIdentifier: 'katex.integral', value: '\\int_{0}^{1} x^2 \\, dx' },
  { stringIdentifier: 'katex.infinity', value: '\\infty' },
  { stringIdentifier: 'katex.pi', value: '\\pi' },
  { stringIdentifier: 'katex.delta', value: '\\Delta x' },
  { stringIdentifier: 'katex.vector', value: '\\vec{v} / \\mathbf{v}' },
  {
    stringIdentifier: 'katex.equality',
    value: '=, \\neq, \\approx, \\leq, \\geq',
  },
  { stringIdentifier: 'katex.shortFraction', value: '\\tfrac{a}{b}' },
  {
    stringIdentifier: 'katex.scaledBrackets',
    value: '\\left( \\frac{a}{b} \\right)',
  },
  {
    stringIdentifier: 'katex.arrows',
    value: '\\rightarrow, \\Leftarrow, \\leftrightarrow',
  },
  {
    stringIdentifier: 'katex.sets',
    value: '\\in, \\notin, \\subset, \\subseteq, \\cup, \\cap',
  },
  {
    stringIdentifier: 'katex.emptysetNumbersets',
    value: '\\emptyset, \\mathbb{R}, \\mathbb{N}, \\mathbb{Z}',
  },
  {
    stringIdentifier: 'katex.absoluteValue',
    value: '\\lvert x \\rvert (oder |x|)',
  },
  {
    stringIdentifier: 'katex.matrices',
    value: '\\begin{bmatrix} a & b \\\\ c & d \\end{bmatrix}',
  },
  {
    stringIdentifier: 'katex.systemOfEquations',
    value: '\\begin{cases} x+y=1 \\\\ x-y=2 \\end{cases}',
  },
  { stringIdentifier: 'katex.fractionInExponent', value: 'x^{\\frac{1}{2}}' },
  { stringIdentifier: 'katex.dots', value: '\\ldots, \\cdots' },
  { stringIdentifier: 'katex.overline', value: '\\overline{x}' },
  { stringIdentifier: 'katex.binomial', value: '\\binom{n}{k}' },
  {
    stringIdentifier: 'katex.logarithm',
    value: '\\log x, \\ln x, \\log_{2}{x}',
  },
  {
    stringIdentifier: 'katex.trigonometric',
    value: '\\sin x, \\cos x, \\tan x',
  },
  { stringIdentifier: 'katex.derivative', value: '\\frac{d}{dx} f(x)' },
  {
    stringIdentifier: 'katex.partialDerivative',
    value: '\\frac{\\partial f}{\\partial x}',
  },
  { stringIdentifier: 'katex.gradient', value: '\\nabla f' },
  {
    stringIdentifier: 'katex.sumWithConditions',
    value: '\\sum\\limits_{i=1}^{n}',
  },
  { stringIdentifier: 'katex.textInMath', value: '\\text{wenn } x > 0' },
  { stringIdentifier: 'katex.exponential', value: 'e^{x}' },
  {
    stringIdentifier: 'katex.bracketedFraction',
    value: '\\left( \\frac{a+b}{c+d} \\right)',
  },
  { stringIdentifier: 'katex.approxIneq', value: '\\approx, \\neq' },
  {
    stringIdentifier: 'katex.doubleArrows',
    value: '\\iff, \\implies, \\impliedby',
  },
  { stringIdentifier: 'katex.brackets', value: '[x], \\{x\\}' },
  { stringIdentifier: 'katex.spacing', value: '\\,, \\;, \\!' },
];

export const openFormulaDialog = async (
  initialLatex?: string,
): Promise<ModalResult<string>> => {
  return useModalStore().openModal<FormulaDialogProps, string>({
    title: $t('katex.enter_equation'),
    size: 'md',
    component: FormulaDialog as Component,
    props: {
      initialLatex,
    },
  });
};
